import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { trackStoreViewOnce } from "@/graphql/analytics";
import { client } from "@/lib/apolloClient";
import StoreHeader from "./components/StoreHeader";
import ProductSearch from "./components/ProductSearch";
import CategoryFilter from "./components/CategoryFilter";
import ProductGrid from "./components/ProductGrid";
import ProductCatalogSections from "./components/ProductCatalogSections";
import { usePublicStore } from "./hooks/usePublicStore";
import { usePublicProducts } from "./hooks/usePublicProducts";
import { useCategoryScrollSpy } from "./hooks/useCategoryScrollSpy";
import {
  CATALOG_TOP_SECTION_ID,
  categorySectionId,
  groupProductsByCategory,
} from "./lib/groupProductsByCategory";

export default function StorefrontPage() {
  const { storeSlug, categorySlug } = useParams<{
    storeSlug: string;
    categorySlug?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const isSearching = debouncedSearch.trim().length > 0;

  const { store, categories, loading: storeLoading, notFound } = usePublicStore(storeSlug);
  const { products, loading: productsLoading, isInitialLoading: productsInitialLoading } =
    usePublicProducts(storeSlug, {
      search: isSearching ? debouncedSearch : undefined,
    });

  useEffect(() => {
    if (!store) return;
    trackStoreViewOnce(client, store.id);
  }, [store?.id]);

  const catalogSections = useMemo(
    () => (isSearching ? [] : groupProductsByCategory(products, categories)),
    [isSearching, products, categories]
  );

  const navCategories = useMemo(() => {
    const slugsWithProducts = new Set(catalogSections.map((s) => s.category.slug));
    const fromStore = categories.filter((c) => slugsWithProducts.has(c.slug));
    if (slugsWithProducts.has("outros")) {
      return [
        ...fromStore,
        {
          id: "uncategorized",
          name: "Outros",
          slug: "outros",
          position: Number.MAX_SAFE_INTEGER,
        },
      ];
    }
    return fromStore;
  }, [catalogSections, categories]);

  const sectionIds = useMemo(() => {
    if (isSearching) return [];
    return [
      CATALOG_TOP_SECTION_ID,
      ...catalogSections.map((s) => categorySectionId(s.category.slug)),
    ];
  }, [isSearching, catalogSections]);

  const { activeSectionId, scrollToSection } = useCategoryScrollSpy(sectionIds);

  const syncHash = (sectionId: string | null) => {
    if (!storeSlug) return;
    const base = `/stores/${storeSlug}`;
    const hash =
      sectionId && sectionId !== CATALOG_TOP_SECTION_ID
        ? `#${sectionId}`
        : "";
    navigate(`${base}${hash}`, { replace: true });
  };

  const handleCategorySelect = (sectionId: string | null) => {
    scrollToSection(sectionId);
    syncHash(sectionId);
  };

  useEffect(() => {
    if (!storeSlug || isSearching || productsLoading) return;

    const hashSection = location.hash.replace(/^#/, "");
    const targetSection = categorySlug
      ? categorySectionId(categorySlug)
      : hashSection || null;

    if (!targetSection) return;

    const frame = window.requestAnimationFrame(() => {
      scrollToSection(
        targetSection === CATALOG_TOP_SECTION_ID ? null : targetSection
      );
      if (categorySlug) {
        navigate(`/stores/${storeSlug}#${targetSection}`, { replace: true });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [storeSlug, categorySlug, location.hash, isSearching, productsLoading, navigate, scrollToSection]);

  const loading = storeLoading || productsLoading;
  const hasFilters = isSearching;

  if (loading && !store) {
    return (
      <div className="container-elegant page-padding">
        <Skeleton className="h-10 w-64 mb-8" />
        <Skeleton className="h-12 w-full max-w-md mb-6" />
        <div className="flex gap-8">
          <Skeleton className="h-48 w-36 shrink-0" />
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="aspect-square w-full" />
            <Skeleton className="aspect-square w-full" />
            <Skeleton className="aspect-square w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !store) {
    return (
      <div className="container-elegant page-padding flex items-center justify-center min-h-[50vh]">
        <p className="text-lg font-light text-primary-600 dark:text-primary-400">
          Loja não encontrada
        </p>
      </div>
    );
  }

  const emptyMessage = hasFilters
    ? "Nenhum produto encontrado para os filtros selecionados."
    : "Nenhum produto disponível";

  const showCategoryNav = !isSearching && navCategories.length > 0;

  return (
    <div className="container-elegant page-padding">
      <StoreHeader store={store} />

      <div className="mb-10">
        <ProductSearch value={searchInput} onSearchChange={setSearchInput} />
      </div>

      {productsInitialLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="aspect-square w-full" />
        </div>
      ) : products.length === 0 ? (
        <p className="text-base font-light text-primary-600 dark:text-primary-400">
          {emptyMessage}
        </p>
      ) : (
        <div
          className={
            showCategoryNav
              ? "flex flex-col md:flex-row md:gap-12 lg:gap-16"
              : undefined
          }
        >
          {showCategoryNav && (
            <aside className="md:w-44 lg:w-52 shrink-0 md:sticky md:top-24 md:self-start mb-8 md:mb-0">
              <CategoryFilter
                categories={navCategories}
                activeSectionId={activeSectionId}
                onSelect={handleCategorySelect}
              />
            </aside>
          )}

          <div className="flex-1 min-w-0">
            {isSearching ? (
              <ProductGrid products={products} storeSlug={store.slug} />
            ) : (
              <>
                <div
                  id={CATALOG_TOP_SECTION_ID}
                  className="scroll-mt-28"
                  aria-hidden
                />
                <ProductCatalogSections
                  sections={catalogSections}
                  storeSlug={store.slug}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
